import { Eye, FileText, CheckCircle, Swords, CreditCard, User } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { Dispute, DisputeWithNavigation } from "@features/disputes/types/disputes.types"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { CopyButton } from "@shared/components/ui/CopyButton"

interface DisputeSpecificHandlers {
  onSubmitEvidence?: (dispute: Dispute | DisputeWithNavigation) => void
  onAccept?: (dispute: Dispute | DisputeWithNavigation) => void
  onChallenge?: (dispute: Dispute | DisputeWithNavigation) => void
  onViewPayment?: (dispute: Dispute | DisputeWithNavigation) => void
  onViewCustomer?: (dispute: Dispute | DisputeWithNavigation) => void
  onPreview?: (dispute: Dispute | DisputeWithNavigation) => void
  onView?: (dispute: Dispute | DisputeWithNavigation) => void
}

export type DisputeActionHandlers = BaseActionHandlers<Dispute | DisputeWithNavigation> & DisputeSpecificHandlers

const isActionableStatus = (status: string) =>
  status === "needs_response" || status === "warning_needs_response"

const createDisputeBulkActions = (): BulkAction[] => [
  {
    key: "submit-evidence",
    label: "Submit Evidence",
    icon: <FileText className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
]

const createDisputeRowActions = (handlers: DisputeActionHandlers): TableAction<Dispute | DisputeWithNavigation>[] => [
  {
    key: "preview",
    label: "Preview",
    icon: <Eye className="h-4 w-4" />,
    onClick: (dispute) => handlers.onPreview?.(dispute),
  },
  {
    key: "submit-evidence",
    label: "Submit evidence",
    icon: <FileText className="h-4 w-4" />,
    onClick: (dispute) => handlers.onSubmitEvidence?.(dispute),
    condition: (dispute) => isActionableStatus(dispute.status),
  },
  {
    key: "accept-dispute",
    label: "Accept dispute",
    icon: <CheckCircle className="h-4 w-4" />,
    onClick: (dispute) => handlers.onAccept?.(dispute),
    condition: (dispute) => isActionableStatus(dispute.status),
  },
  {
    key: "challenge-dispute",
    label: "Challenge dispute",
    icon: <Swords className="h-4 w-4" />,
    onClick: (dispute) => handlers.onChallenge?.(dispute),
    condition: (dispute) => isActionableStatus(dispute.status),
  },
  {
    key: "view-payment",
    label: "View payment",
    icon: <CreditCard className="h-4 w-4" />,
    onClick: (dispute) => handlers.onViewPayment?.(dispute),
    condition: (dispute) => Boolean(dispute.paymentId),
  },
  {
    key: "view-customer",
    label: "View customer",
    icon: <User className="h-4 w-4" />,
    onClick: (dispute) => handlers.onViewCustomer?.(dispute),
    condition: (dispute) => Boolean(dispute.customer?.id),
  },
]

export const getDisputeActions = (handlers: DisputeActionHandlers = {}) => {
  const baseActions = createBaseActions<Dispute | DisputeWithNavigation>('Dispute', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: true,
    showAddNote: true,
    showCopyId: true,
    showExport: true,
  })

  const disputeSpecificActions = {
    bulkActions: createDisputeBulkActions(),
    rowActions: createDisputeRowActions(handlers),
  }

  return mergeActions(baseActions, disputeSpecificActions)
}

export const getDisputeBulkActions = (handlers: DisputeActionHandlers = {}): BulkAction[] =>
  getDisputeActions(handlers).bulkActions

export const getDisputeRowActions = (handlers: DisputeActionHandlers = {}): TableAction<Dispute | DisputeWithNavigation>[] =>
  getDisputeActions(handlers).rowActions

export const getDisputeHoverActions = (handlers: DisputeActionHandlers = {}): TableAction<Dispute | DisputeWithNavigation>[] => [
  {
    key: "copy-id",
    label: "Copy Dispute ID",
    onClick: () => {},
    renderAction: (dispute) => (
      <CopyButton value={dispute.id} className="h-7 w-7" buttonSize="sm" />
    ),
  },
  {
    key: "submit-evidence",
    label: "Submit evidence",
    icon: <FileText className="h-4 w-4" />,
    onClick: (dispute) => handlers.onSubmitEvidence?.(dispute),
    condition: (dispute) => isActionableStatus(dispute.status),
  },
]
